import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

/**
 * Our Journey — horizontal timeline (Option A).
 * Milestones flow left→right along a blue/green gradient rail. The last
 * milestone is treated as the "latest" and gets a filled dot + eaf6fc card.
 * Scrolls horizontally on narrow desktops / tablets.
 */
export default function JourneyTimeline() {
  const [milestones, setMilestones] = useState([]);
  const railRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/journey");
        setMilestones(data || []);
      } catch { /* silent */ }
    })();
  }, []);

  if (!milestones.length) return null;

  return (
    <section
      data-testid="about-journey-section"
      className="bg-white border-y border-black/10"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-24 pb-10">
        <Reveal>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4796d1] mb-4">
            Our Journey
          </p>
          <h2 className="font-display font-bold text-4xl lg:text-6xl tracking-tight text-[#334155]">
            Fifty years of engineering.
          </h2>
          <p className="mt-6 max-w-2xl text-[#52525B] leading-relaxed text-lg">
            Milestones that shaped Manik Engineering — from a single workshop in Bhandup
            to a pan-India modular manufacturer.
          </p>
        </Reveal>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-24">
        <div className="overflow-x-auto pb-6 journey-scroll" ref={railRef}>
          <div
            className="relative pt-14"
            style={{ minWidth: `${Math.max(milestones.length * 220, 900)}px` }}
          >
            {/* rail */}
            <div className="absolute left-0 right-0 top-[70px] h-[3px] bg-gradient-to-r from-[#4796d1] via-[#86b93f] to-[#4796d1]" />

            <div
              className="grid gap-4 relative"
              style={{ gridTemplateColumns: `repeat(${milestones.length}, minmax(0, 1fr))` }}
            >
              {milestones.map((m, i) => {
                const isLatest = i === milestones.length - 1;
                const accent = i % 2 === 0 ? "#4796d1" : "#86b93f";
                return (
                  <Reveal
                    key={m.id}
                    delay={i * 0.06}
                    className="relative"
                  >
                    <div
                      className="font-mono-accent text-xs font-semibold mb-3"
                      style={{ color: accent }}
                      data-testid={`journey-year-${i}`}
                    >
                      {m.year}{isLatest ? " · latest" : ""}
                    </div>
                    <span
                      className="block w-6 h-6 rounded-full border-4 relative z-10"
                      style={{
                        background: isLatest ? accent : "#fff",
                        borderColor: accent,
                        boxShadow: `0 0 0 6px ${accent}22`,
                      }}
                      aria-hidden
                    />
                    <div
                      data-testid={`journey-card-${i}`}
                      className={`mt-6 p-5 border ${isLatest ? "bg-[#eaf6fc]" : "bg-[#F9FAFB]"}`}
                      style={{ borderColor: isLatest ? `${accent}66` : "rgba(0,0,0,.1)" }}
                    >
                      <p className="font-display font-bold text-lg leading-tight text-[#334155]">{m.title}</p>
                      {m.note && (
                        <p className="text-sm text-[#52525B] mt-2 leading-relaxed">{m.note}</p>
                      )}
                    </div>
                  </Reveal>
                );
              })}
            </div>
            {milestones.length > 5 && (
              <p className="mt-8 font-mono-accent text-[10px] uppercase tracking-widest text-[#94A3B8]">
                ← scroll to view all {milestones.length} milestones →
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
